import { useState, useEffect, useMemo } from "react";
import { US_STATES } from "@shared/schema";
import type { Policy, PolicyAdditionalInsured } from "@shared/schema";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import {
  useCreateAdditionalInsured,
  useUpdateAdditionalInsured,
  useDeleteAdditionalInsured,
  type InsuredAdditionalInsuredLink,
} from "@/lib/api";
import {
  buildPolicyCoverageGroups,
  countOfferedSelections,
  toggleOfferedSelection,
} from "@/lib/policy-coverages";
import { Loader2, Trash2 } from "lucide-react";
import { useConfirm } from "@/components/ui/confirm-dialog";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  insuredId: string;
  policies?: Policy[];
  editData?: InsuredAdditionalInsuredLink | null;
  /** Pre-fill just the name (e.g. completing a name-only carrier entry). */
  prefillName?: string | null;
  /**
   * Pre-fill name + address from a carrier-supplied AI (CSR-743). User still
   * submits as a CREATE (not UPDATE) — this is just a head-start so they
   * don't have to retype the address Progressive already gave us.
   */
  prefillData?: PolicyAdditionalInsured | null;
}

type AdditionalInsuredStatus = "active" | "inactive";

interface FormErrors {
  name?: string;
  street?: string;
  city?: string;
  state?: string;
  zip?: string;
  coverages?: string;
}

export function AdditionalInsuredDialog({ open, onOpenChange, insuredId, policies = [], editData, prefillName, prefillData }: Props) {
  const { toast } = useToast();
  const { confirm, confirmDialog } = useConfirm();
  const createMutation = useCreateAdditionalInsured(insuredId);
  const updateMutation = useUpdateAdditionalInsured(insuredId);
  const deleteMutation = useDeleteAdditionalInsured(insuredId);

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [street, setStreet] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [zip, setZip] = useState("");
  const [specialVerbiage, setSpecialVerbiage] = useState("");
  const [selectedCoverages, setSelectedCoverages] = useState<string[]>([]);
  const [status, setStatus] = useState<AdditionalInsuredStatus>("active");
  const [errors, setErrors] = useState<FormErrors>({});

  const isEdit = !!editData;
  const initialStatus: AdditionalInsuredStatus = editData?.inactive ? "inactive" : "active";

  // CSR-1994 — the insured Assets tab now passes every policy term, including
  // superseded `renewed` ones. Drop only those: their coverages belong to a term
  // that no longer exists, so an additional insured must not be linked to them.
  // Every other status keeps the CSR-792 filter this picker was specified with
  // (drop `cancelled` only) — narrowing to COI_ELIGIBLE_STATUSES here would also
  // silently drop expired, replaced, renewal-offer, and null-status legacy rows.
  const coverageGroups = useMemo(
    () =>
      buildPolicyCoverageGroups(
        policies.filter((p) => p.status?.toLowerCase() !== "renewed"),
      ),
    [policies],
  );
  const allCoverageIds = useMemo(
    () => coverageGroups.flatMap((g) => g.coverages.map((c) => c.id)),
    [coverageGroups],
  );
  // CSR-2148 — a saved link may name a coverage the carrier has since removed;
  // it is kept in the payload but no longer offered, so count what is on offer.
  const selectedOfferedCount = useMemo(
    () => countOfferedSelections(selectedCoverages, allCoverageIds),
    [selectedCoverages, allCoverageIds],
  );

  useEffect(() => {
    if (open) {
      if (editData) {
        setName(editData.name || "");
        setEmail(editData.email || "");
        setStreet(editData.street || "");
        setCity(editData.city || "");
        setState(editData.state || "");
        setZip(editData.zip || "");
        setSpecialVerbiage(editData.specialVerbiage || "");
        setStatus(editData.inactive ? "inactive" : "active");
        setSelectedCoverages(
          Array.isArray(editData.coverageIds)
            ? editData.coverageIds.map(String)
            : [],
        );
      } else if (prefillData) {
        // Carrier-supplied head-start (CSR-743). Submit creates a fresh CRUD
        // entry — there's no existing linkId to update.
        setName(prefillData.name || "");
        setEmail("");
        setStreet(prefillData.street || "");
        setCity(prefillData.city || "");
        setState(prefillData.state || "");
        setZip(prefillData.zip || "");
        setSpecialVerbiage("");
        setSelectedCoverages([]);
        setStatus("active");
      } else {
        setName(prefillName || "");
        setEmail("");
        setStreet("");
        setCity("");
        setState("");
        setZip("");
        setSpecialVerbiage("");
        setSelectedCoverages([]);
        setStatus("active");
      }
      setErrors({});
    }
  }, [open, editData, prefillName, prefillData]);

  const toggleCoverage = (id: string) => {
    setSelectedCoverages((prev) =>
      prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id],
    );
  };

  const selectAllCoverages = () => {
    setSelectedCoverages(toggleOfferedSelection(selectedCoverages, allCoverageIds));
  };

  const validate = (): boolean => {
    const newErrors: FormErrors = {};
    if (!name.trim()) newErrors.name = "Name is required";
    if (!street.trim()) newErrors.street = "Street is required";
    if (!city.trim()) newErrors.city = "City is required";
    if (!state) newErrors.state = "State is required";
    if (!zip.trim()) newErrors.zip = "ZIP is required";
    // CSR-2148 — count what the picker offers: a link retained against a
    // removed coverage must not satisfy a rule about what the user can see.
    if (coverageGroups.length > 0 && selectedOfferedCount === 0) {
      newErrors.coverages = "Select at least one coverage";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // CSR-2274 — the status can change at any time, so a status-only edit must
  // not be held back by the record's missing address or coverages (Pending).
  const isStatusOnlyChange = (): boolean => {
    if (!editData || status === initialStatus) return false;
    const savedCoverages = Array.isArray(editData.coverageIds) ? editData.coverageIds.map(String) : [];
    return (
      name === (editData.name || "") &&
      email === (editData.email || "") &&
      street === (editData.street || "") &&
      city === (editData.city || "") &&
      state === (editData.state || "") &&
      zip === (editData.zip || "") &&
      specialVerbiage === (editData.specialVerbiage || "") &&
      selectedCoverages.length === savedCoverages.length &&
      selectedCoverages.every((id) => savedCoverages.includes(id))
    );
  };

  const handleSubmit = async () => {
    if (isStatusOnlyChange()) {
      try {
        await updateMutation.mutateAsync({ linkId: editData!.linkId, data: { status } });
        toast({ title: "Updated", description: "Additional insured status updated." });
        onOpenChange(false);
      } catch (err) {
        toast({ title: "Error", description: (err as Error).message, variant: "destructive" });
      }
      return;
    }
    if (!validate()) return;

    try {
      if (isEdit) {
        await updateMutation.mutateAsync({
          linkId: editData!.linkId,
          data: {
            name: name.trim(),
            email: email.trim() || undefined,
            street: street.trim(),
            city: city.trim(),
            state,
            zip: zip.trim(),
            specialVerbiage: specialVerbiage.trim() || null,
            coverageIds: selectedCoverages,
            ...(status !== initialStatus ? { status } : {}),
          },
        });
        toast({ title: "Updated", description: "Additional insured updated successfully." });
      } else {
        const created = await createMutation.mutateAsync({
          name: name.trim(),
          email: email.trim() || undefined,
          street: street.trim(),
          city: city.trim(),
          state,
          zip: zip.trim(),
          specialVerbiage: specialVerbiage.trim() || null,
          coverageIds: selectedCoverages,
        });
        // CSR-2274 — the create endpoint takes no status, so an Inactive choice
        // is applied to the new link right after it exists.
        if (status === "inactive") {
          try {
            await updateMutation.mutateAsync({ linkId: created.id, data: { status } });
          } catch (err) {
            toast({
              title: "Added, but the status wasn't set",
              description: (err as Error).message,
              variant: "destructive",
            });
            onOpenChange(false);
            return;
          }
        }
        toast({ title: "Added", description: "Additional insured added successfully." });
      }
      onOpenChange(false);
    } catch (err) {
      toast({
        title: "Error",
        description: (err as Error).message,
        variant: "destructive",
      });
    }
  };

  const handleDelete = async () => {
    if (!editData) return;
    const ok = await confirm({
      title: "Remove Additional Insured",
      description: `Remove "${editData.name}" as an additional insured?`,
      confirmLabel: "Remove",
      destructive: true,
    });
    if (!ok) return;

    try {
      await deleteMutation.mutateAsync(editData.linkId);
      toast({ title: "Deleted", description: "Additional insured removed." });
      onOpenChange(false);
    } catch (err) {
      toast({
        title: "Error",
        description: (err as Error).message,
        variant: "destructive",
      });
    }
  };

  const isBusy = createMutation.isPending || updateMutation.isPending || deleteMutation.isPending;

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="additional-insured-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit Additional Insured" : "Add Additional Insured"}</DialogTitle>
          <DialogDescription>
            {isEdit
              ? "Update the certificate holder details and the coverages this Additional Insured applies to."
              : prefillData
                ? "Name and address were imported from the carrier. Add an email or special verbiage if needed, then click Add to save."
                : prefillName
                  ? "Complete the missing information for this Additional Insured so it can be used on certificates."
                  : "Add a certificate holder to this insured. You can send Certificates of Insurance to them later."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div>
            <label className="text-sm font-medium">
              Name <span className="text-destructive">*</span>
            </label>
            <Input
              className="mt-1"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Amazon Logistics"
              data-testid="ai-dialog-name"
            />
            {errors.name && <p className="text-xs text-destructive mt-1">{errors.name}</p>}
          </div>

          <div>
            <label className="text-sm font-medium" htmlFor="ai-dialog-status">Status</label>
            <Select value={status} onValueChange={(v) => setStatus(v as AdditionalInsuredStatus)}>
              <SelectTrigger id="ai-dialog-status" className="mt-1" data-testid="ai-dialog-status">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground mt-1">
              The next carrier sync re-checks this against the policy and may change it back.
            </p>
          </div>

          <div>
            <label className="text-sm font-medium">Email</label>
            <Input
              className="mt-1"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="contact@example.com"
              data-testid="ai-dialog-email"
            />
          </div>

          <div>
            <label className="text-sm font-medium">
              Street <span className="text-destructive">*</span>
            </label>
            <Input
              className="mt-1"
              value={street}
              onChange={(e) => setStreet(e.target.value)}
              placeholder="123 Main St"
              data-testid="ai-dialog-street"
            />
            {errors.street && <p className="text-xs text-destructive mt-1">{errors.street}</p>}
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-sm font-medium">
                City <span className="text-destructive">*</span>
              </label>
              <Input
                className="mt-1"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                placeholder="City"
                data-testid="ai-dialog-city"
              />
              {errors.city && <p className="text-xs text-destructive mt-1">{errors.city}</p>}
            </div>
            <div>
              <label className="text-sm font-medium">
                State <span className="text-destructive">*</span>
              </label>
              <select
                className="mt-1 h-9 w-full rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                value={state}
                onChange={(e) => setState(e.target.value)}
                data-testid="ai-dialog-state"
              >
                <option value="">Select...</option>
                {US_STATES.map((st) => (
                  <option key={st.value} value={st.value}>
                    {st.value}
                  </option>
                ))}
              </select>
              {errors.state && <p className="text-xs text-destructive mt-1">{errors.state}</p>}
            </div>
            <div>
              <label className="text-sm font-medium">
                ZIP <span className="text-destructive">*</span>
              </label>
              <Input
                className="mt-1"
                value={zip}
                onChange={(e) => setZip(e.target.value)}
                placeholder="12345"
                data-testid="ai-dialog-zip"
              />
              {errors.zip && <p className="text-xs text-destructive mt-1">{errors.zip}</p>}
            </div>
          </div>

          <div>
            <label className="text-sm font-medium">Special Verbiage</label>
            <p className="text-xs text-muted-foreground mt-0.5">
              Optional free-form text to include on certificates issued to this Additional Insured.
            </p>
            <textarea
              className="mt-1 w-full min-h-[80px] px-3 py-2 rounded-md border border-input bg-background text-sm resize-y focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
              value={specialVerbiage}
              onChange={(e) => setSpecialVerbiage(e.target.value)}
              placeholder="e.g. Certificate holder is named as Additional Insured per written contract..."
              data-testid="ai-dialog-verbiage"
            />
          </div>

          {coverageGroups.length > 0 && (
            <div>
              <label className="text-sm font-medium">
                Coverages <span className="text-destructive">*</span>
              </label>
              <p className="text-xs text-muted-foreground mt-0.5">
                Select which coverages this Additional Insured applies to.
              </p>
              <div className="mt-2 rounded-md border border-input overflow-hidden">
                <div className="flex items-center justify-between px-3 py-2 bg-muted/30 border-b border-input">
                  <span className="text-xs text-muted-foreground">
                    {selectedOfferedCount} of {allCoverageIds.length} selected
                  </span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-6 text-xs px-2"
                    onClick={selectAllCoverages}
                  >
                    {selectedOfferedCount === allCoverageIds.length ? "Deselect all" : "Select all"}
                  </Button>
                </div>
                <div className="max-h-48 overflow-y-auto">
                  {coverageGroups.map((group) => (
                    <div key={group.policyId}>
                      <div className="px-3 py-1.5 bg-muted/20 border-b border-input">
                        <span className="text-xs font-semibold text-muted-foreground uppercase">
                          {group.policyLabel}
                        </span>
                      </div>
                      {group.coverages.map((cov) => (
                        <label
                          key={cov.id}
                          className="flex items-center gap-2 px-3 py-2 hover:bg-muted/30 cursor-pointer text-sm"
                        >
                          <Checkbox
                            checked={selectedCoverages.includes(cov.id)}
                            onCheckedChange={() => toggleCoverage(cov.id)}
                          />
                          {cov.name}
                        </label>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
              {errors.coverages && <p className="text-xs text-destructive mt-1">{errors.coverages}</p>}
            </div>
          )}
        </div>

        <DialogFooter className="flex justify-between sm:justify-between">
          {isEdit ? (
            <Button
              variant="ghost"
              size="sm"
              className="text-destructive hover:text-destructive hover:bg-destructive/10 gap-1.5"
              onClick={handleDelete}
              disabled={isBusy}
              data-testid="ai-dialog-delete"
            >
              <Trash2 className="w-4 h-4" /> Delete
            </Button>
          ) : (
            <div />
          )}
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isBusy}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={isBusy} data-testid="ai-dialog-submit">
              {isBusy ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-1" />
                  {isEdit ? "Saving..." : "Adding..."}
                </>
              ) : isEdit ? (
                "Save"
              ) : (
                "Add"
              )}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    {confirmDialog}
    </>
  );
}
